import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ref, push, set } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import { useLanguage } from '../LanguageContext';

export default function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [seats, setSeats] = useState(1);
  const [sending, setSending] = useState(false);

  const ride = {
    rideId: searchParams.get('rideId') || '',
    from: searchParams.get('from') || '—',
    to: searchParams.get('to') || '—',
    date: searchParams.get('date') || '—',
    time: searchParams.get('time') || '—',
    driver: searchParams.get('driver') || '—',
    driverPhone: searchParams.get('driverPhone') || '',
    price: parseInt(searchParams.get('price')) || 0,
    seatsAvailable: parseInt(searchParams.get('seatsAvailable')) || 1,
    vehicle: searchParams.get('vehicle') || '—',
    rating: searchParams.get('rating') || '—',
  };

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  const isGuest = user && (!user.name || user.name === 'Guest User');

  const sendRequest = async () => {
    if (isGuest) {
      alert('Please login to book a ride.');
      navigate('/');
      return;
    }
    setSending(true);
    try {
      const requestsRef = ref(db, 'bookingRequests/');
      const newRef = push(requestsRef);
      await set(newRef, {
        rideId: ride.rideId,
        from: ride.from,
        to: ride.to,
        date: ride.date,
        time: ride.time,
        driver: ride.driver,
        driverPhone: ride.driverPhone,
        passengerName: user.name,
        passengerPhone: user.phone,
        seats: seats,
        pricePerSeat: ride.price,
        totalAmount: seats * ride.price,
        status: 'pending_approval',
        requestedAt: new Date().toISOString(),
      });
      setSending(false);
      alert('Request sent to ' + ride.driver + '! You will be notified once approved.');
      navigate('/my-bookings');
    } catch (error) {
      setSending(false);
      alert('Error: ' + error.message);
    }
  };

  if (!user) return null;

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>

        <h2 style={styles.pageTitle}>{t('bookingDetails')}</h2>

        <div style={styles.card}>
          <div style={styles.route}>{ride.from} → {ride.to}</div>
          <div style={styles.meta}>📅 {ride.date} · ⏰ {ride.time}</div>
          <div style={styles.meta}>🚗 {ride.vehicle}</div>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>{t('driverInformation')}</h3>
          <div style={styles.driverRow}>
            <div style={styles.driverAvatar}>
              {ride.driver.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={styles.driverName}>{ride.driver}</div>
              <div style={styles.driverPhone}>{ride.driverPhone}</div>
              <div style={styles.driverRating}>⭐ {ride.rating} rating</div>
            </div>
            <button
              style={styles.chatBtn}
              onClick={() => navigate(
                '/chat?chatWithName=' + encodeURIComponent(ride.driver) +
                '&chatWithPhone=' + encodeURIComponent(ride.driverPhone) +
                '&from=' + encodeURIComponent(ride.from) +
                '&to=' + encodeURIComponent(ride.to)
              )}
            >💬 Chat</button>
          </div>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>{t('selectSeats')}</h3>
          <div style={styles.counter}>
            <button style={styles.counterBtn} onClick={() => setSeats(Math.max(1, seats - 1))}>−</button>
            <span style={styles.counterVal}>{seats}</span>
            <button style={styles.counterBtn} onClick={() => setSeats(Math.min(ride.seatsAvailable, seats + 1))}>+</button>
          </div>
          <p style={styles.seatsNote}>{ride.seatsAvailable} {t('seatsAvailable')}</p>
        </div>

        <div style={styles.card}>
          <div style={styles.totalRow}>
            <span style={styles.totalLabel}>{t('totalAmount')}</span>
            <span style={styles.totalAmount}>₹{seats * ride.price}</span>
          </div>
        </div>

        <p style={styles.reassurance}>
          Driver reviews your request before payment. Free cancellation until then.
        </p>

        <button style={styles.bookBtn} onClick={sendRequest} disabled={sending}>
          {sending ? 'Sending request...' : '📨 ' + t('requestBooking') + ' · ₹' + (seats * ride.price)}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  container: { maxWidth: 520, margin: '0 auto', padding: '30px 20px 40px' },
  pageTitle: { color: '#1a1a2e', marginBottom: 20 },
  card: { border: '1px solid #eef1f8', borderRadius: 14, padding: 18, marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' },
  cardTitle: { margin: '0 0 14px', color: '#1a1a2e', fontSize: 15 },
  route: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 },
  meta: { fontSize: 13, color: '#5a5f7d', marginBottom: 3 },
  driverRow: { display: 'flex', gap: 12, alignItems: 'center' },
  driverAvatar: {
    width: 50, height: 50, borderRadius: 25, background: '#3a8fd0', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, flexShrink: 0,
  },
  driverName: { fontSize: 15, fontWeight: 700, color: '#1a1a2e' },
  driverPhone: { fontSize: 13, color: '#5a5f7d', marginTop: 2 },
  driverRating: { fontSize: 12, color: '#c98a1a', fontWeight: 600, marginTop: 2 },
  chatBtn: { background: '#eaf5fc', border: '1px solid #d9edfa', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13, color: '#3a8fd0' },
  counter: { display: 'flex', alignItems: 'center', gap: 20 },
  counterBtn: { width: 38, height: 38, borderRadius: 19, border: '1.5px solid #d9edfa', background: '#ffffff', fontSize: 18, cursor: 'pointer' },
  counterVal: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', minWidth: 28, textAlign: 'center' },
  seatsNote: { fontSize: 12, color: '#9499b5', marginTop: 10 },
  totalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  totalLabel: { fontSize: 15, fontWeight: 600, color: '#1a1a2e' },
  totalAmount: { fontSize: 22, fontWeight: 800, color: '#3a8fd0' },
  reassurance: { fontSize: 11, color: '#5a5f7d', textAlign: 'center', marginBottom: 12 },
  bookBtn: { width: '100%', height: 50, background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
};